/**
 * Exclusion relationship rule (based on category)
 */
import { uniq } from 'lodash';
import { RuleType, ReasonCode } from '../constants';
import { PlanRule } from './PlanRule';
import { Reason } from '../models/Reason';

export class ExclusionRule extends PlanRule {
  /**
   * @param {Array<string>|Function} categoriesOrGetConflictingPlans - Either an array of categories, or a function that receives (cart, planToAdd) and returns conflicting plans
   * @param {Object} options - Options for the rule
   * @param {boolean} options.cascadeRemoval - Whether to enable cascade removal
   * @param {boolean} options.sameCategoryReplacement - Whether to enable same category replacement
   * @param {Function} options.getConflictingPlans - Function that receives (cart, planToAdd) and returns an array of conflicting plans (alternative to passing function as first param)
   */
  constructor(categoriesOrGetConflictingPlans, options = {}) {
    // Support both: new ExclusionRule(categories, options) and new ExclusionRule(getConflictingPlans, options)
    const isFunction = typeof categoriesOrGetConflictingPlans === 'function';
    const getConflictingPlans = isFunction ? categoriesOrGetConflictingPlans : options.getConflictingPlans;
    const categories = isFunction ? [] : categoriesOrGetConflictingPlans;

    const descriptionPrefix = getConflictingPlans
      ? options.description || 'Custom exclusion rule with dynamic logic'
      : `Categories ${categories.join(', ')} cannot coexist`;

    let description = descriptionPrefix;
    if (options.source) {
      const sources = Array.isArray(options.source) ? options.source : [options.source];
      const sourceStrings = sources.map((s) => `${s.plan}${s.className ? ` -> ${s.className}` : ''}`);
      description += ` (Source: ${sourceStrings.join(', ')})`;
    }

    super(RuleType.EXCLUSION, description);
    this.categories = categories;
    this.getConflictingPlans = getConflictingPlans; // Custom function to get conflicting plans
    this.cascadeRemoval = options.cascadeRemoval !== undefined ? options.cascadeRemoval : false; // Whether to enable cascade removal
    this.sameCategoryReplacement = options.sameCategoryReplacement || false; // Whether to enable same category replacement
    this.source = options.source;
  }

  validate(cart, planToAdd) {
    // Collect all conflicting plans from both same category and cross category
    const allConflictingPlans = [];

    // If custom function is provided, use it to get conflicting plans
    if (this.getConflictingPlans) {
      const conflictingPlans = this.getConflictingPlans(cart, planToAdd);
      if (Array.isArray(conflictingPlans) && conflictingPlans.length > 0) {
        allConflictingPlans.push(...conflictingPlans);
        // Set reason directly on each conflicting plan
        conflictingPlans.forEach((plan) => {
          plan.reason = new Reason(
            ReasonCode.EXCLUSION_CATEGORY_CONFLICT,
            this,
            `Removed due to conflict with ${planToAdd.planCode}`,
            planToAdd.planCode,
          );
        });
      }
    } else {
      // Original category-based logic
      if (!this.categories.includes(planToAdd.category)) {
        return { valid: true };
      }

      // Check replacement within same category
      if (this.sameCategoryReplacement) {
        //check if exist same category but sameCategoryReplacement = false
        const existNotReplaceCase = cart.rules.find(
          (rule) =>
            rule instanceof ExclusionRule &&
            rule.categories.includes(planToAdd.category) &&
            rule.sameCategoryReplacement === false,
        );
        const sameCategoryPlans = cart.getPlansByCategories([planToAdd.category]);

        if (!existNotReplaceCase && sameCategoryPlans.length > 0) {
          allConflictingPlans.push(...sameCategoryPlans);
          // Set reason directly on each conflicting plan
          sameCategoryPlans.forEach((plan) => {
            plan.reason = new Reason(
              ReasonCode.EXCLUSION_CATEGORY_CONFLICT,
              this,
              `Replaced by ${planToAdd.planCode}`,
              planToAdd.planCode,
            );
          });
        }
      }

      // Check exclusion between different categories
      const crossCategoryPlans = cart.getPlansByCategories(
        this.categories.filter((category) => category !== planToAdd.category),
      );

      if (crossCategoryPlans.length > 0) {
        if (this.cascadeRemoval) {
          // Enable cascade removal, add to conflicting plans
          allConflictingPlans.push(...crossCategoryPlans);
          // Set reason directly on each conflicting plan
          crossCategoryPlans.forEach((plan) => {
            plan.reason = new Reason(
              ReasonCode.EXCLUSION_CATEGORY_CONFLICT,
              this,
              `Removed due to conflict with ${planToAdd.planCode}`,
              planToAdd.planCode,
            );
          });
        } else {
          // Traditional mode: not allowed to add
          return {
            valid: false,
            error: `Cannot add ${planToAdd.planCode}: conflicts with existing plans ${crossCategoryPlans
              .map((p) => p.planCode)
              .join(', ')}`,
          };
        }
      }
    }

    // Handle cascade removal check for custom function mode
    if (this.getConflictingPlans && allConflictingPlans.length > 0) {
      if (!this.cascadeRemoval) {
        // Traditional mode: not allowed to add
        return {
          valid: false,
          error: `Cannot add ${planToAdd.planCode}: conflicts with existing plans ${allConflictingPlans
            .map((p) => p.planCode)
            .join(', ')}`,
        };
      }
    }

    // Check extra?.bundleCode in allConflictingPlans, remove same bundleCode plans in cart's other plans
    if (allConflictingPlans.length > 0) {
      // Collect all bundleCodes from conflicting plans
      const bundleCodes = uniq(
        allConflictingPlans.map((plan) => plan.extra?.bundleCode).filter((bundleCode) => bundleCode),
      );

      // Find all plans in cart with the same bundleCode
      if (bundleCodes.length > 0) {
        // Exclude plans that are being added in this operation
        const toAddPlans = cart.context?.toAddPlans || [];
        const toAddPlanCodes = new Set(toAddPlans.map((p) => p.planCode || p.plan_code));
        const excludePlans = [...allConflictingPlans];
        let bundleCodePlans = cart.getPlansByBundleCodes(bundleCodes, excludePlans);
        // Filter out plans that are being added in this operation
        bundleCodePlans = bundleCodePlans.filter((plan) => !toAddPlanCodes.has(plan.planCode));
        if (bundleCodePlans.length > 0) {
          // Set reason directly on each bundleCode plan
          // Find the triggerBy plan from allConflictingPlans that has the same bundleCode
          bundleCodePlans.forEach((plan) => {
            const triggerByPlan = allConflictingPlans.find((p) => p.extra?.bundleCode === plan.extra?.bundleCode);
            plan.reason = new Reason(
              ReasonCode.BUNDLE_REMOVE,
              this,
              `Removed due to same bundleCode as ${triggerByPlan ? triggerByPlan.planCode : planToAdd.planCode}`,
              triggerByPlan ? triggerByPlan.planCode : planToAdd.planCode,
            );
          });
          allConflictingPlans.push(...bundleCodePlans);
        }
      }
    }

    // Return merged result if there are any conflicting plans
    if (allConflictingPlans.length > 0) {
      return {
        valid: true,
        cascadeRemoval: {
          conflictingPlans: allConflictingPlans,
          rule: this,
        },
      };
    }

    return { valid: true };
  }
}
